import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
}
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers },
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}

function fmtHours(seconds) {
  if (!seconds) return '—';
  return (seconds / 3600).toFixed(1) + 'h';
}
function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function fmtDate(str) {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  return `${parseInt(m)}/${parseInt(d)}`;
}
function isoDate(d) {
  return d.toISOString().slice(0, 10);
}
function weekRange() {
  const now = new Date();
  const day = now.getDay();
  const start = new Date(now);
  start.setDate(now.getDate() - day);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start: isoDate(start), end: isoDate(end) };
}

export default function AdminTimesheets({ embedded = false }) {
  const navigate = useNavigate();
  const Wrap = embedded ? React.Fragment : AdminLayout;
  const { start: defStart, end: defEnd } = weekRange();
  const [startDate, setStartDate] = useState(defStart);
  const [endDate, setEndDate]     = useState(defEnd);
  const [sheets, setSheets]       = useState([]);
  const [onClock, setOnClock]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [groupBy, setGroupBy]     = useState('person'); // 'person' | 'project'

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ts, oc] = await Promise.all([
        apiFetch(`/time/timesheets?startDate=${startDate}&endDate=${endDate}`),
        apiFetch('/time/on-clock'),
      ]);
      setSheets(ts);
      setOnClock(oc);
    } catch (e) {
      setError(e?.error || 'Failed to load timesheets.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Filter
  const filtered = sheets.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.userName.toLowerCase().includes(q) || s.jobcodeName.toLowerCase().includes(q);
  });

  // Group
  function groupedByPerson() {
    const map = {};
    for (const s of filtered) {
      if (!map[s.ourUserId || s.tsUserId]) {
        map[s.ourUserId || s.tsUserId] = { name: s.userName, entries: [], totalSec: 0 };
      }
      map[s.ourUserId || s.tsUserId].entries.push(s);
      map[s.ourUserId || s.tsUserId].totalSec += s.duration || 0;
    }
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }

  function groupedByProject() {
    const map = {};
    for (const s of filtered) {
      if (!map[s.jobcodeName]) {
        map[s.jobcodeName] = { name: s.jobcodeName, entries: [], totalSec: 0 };
      }
      map[s.jobcodeName].entries.push(s);
      map[s.jobcodeName].totalSec += s.duration || 0;
    }
    return Object.values(map).sort((a, b) => b.totalSec - a.totalSec);
  }

  const groups = groupBy === 'person' ? groupedByPerson() : groupedByProject();
  const totalHours = filtered.reduce((sum, s) => sum + (s.duration || 0), 0);

  return (
    <Wrap>
      <div style={{ padding: embedded ? '16px 16px 60px' : '32px 32px 60px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            {embedded && <button onClick={() => navigate('/dashboard')} style={{ background: '#f3f4f6', border: 'none', color: '#e87722', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: '8px 14px', borderRadius: 20, marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>‹ Home</button>}
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: 'var(--text)' }}>Timesheets</h1>
            <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Live from T-Sheets</p>
          </div>
          <button onClick={loadData} style={{ background: 'none', border: '1.5px solid var(--border)', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>
            Refresh
          </button>
        </div>

        {/* On the clock now */}
        {onClock.length > 0 && (
          <div style={{ marginBottom: 28, background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Currently Clocked In — {onClock.length} worker{onClock.length !== 1 ? 's' : ''}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {onClock.map(s => (
                <div key={s.id} style={{ background: '#fff', border: '1px solid #bbf7d0', borderRadius: 20, padding: '4px 14px', fontSize: 13, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a' }} />
                  <span style={{ fontWeight: 600 }}>{s.userName}</span>
                  <span style={{ color: '#4ade80', fontSize: 12 }}>· {s.jobcodeName}</span>
                  <span style={{ color: '#86efac', fontSize: 12 }}>since {fmtTime(s.start)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>From</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, color: 'var(--text)' }} />
            <label style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>To</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, color: 'var(--text)' }} />
          </div>

          <input
            placeholder="Search name or job…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, color: 'var(--text)', minWidth: 180, flex: 1 }}
          />

          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 8, padding: 2 }}>
            {['person', 'project'].map(g => (
              <button key={g} onClick={() => setGroupBy(g)}
                style={{
                  padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: groupBy === g ? '#fff' : 'transparent',
                  color: groupBy === g ? 'var(--text)' : 'var(--text-muted)',
                  boxShadow: groupBy === g ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                }}>
                {g === 'person' ? 'By Person' : 'By Job'}
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Hours', value: fmtHours(totalHours) },
            { label: 'Entries', value: filtered.length },
            { label: 'Workers', value: new Set(filtered.map(s => s.ourUserId || s.tsUserId)).size },
            { label: 'Jobs', value: new Set(filtered.map(s => s.jobcodeName)).size },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '14px 20px', minWidth: 100 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{value}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
            </div>
          ))}
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14, marginBottom: 16 }}>{error}</div>
        )}

        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 15, padding: '40px 0', textAlign: 'center' }}>Loading timesheets…</div>
        ) : groups.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 15, padding: '40px 0', textAlign: 'center' }}>No timesheet entries found for this period.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {groups.map(group => (
              <GroupCard key={group.name} group={group} groupBy={groupBy} />
            ))}
          </div>
        )}
      </div>
    </Wrap>
  );
}

function GroupCard({ group, groupBy }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e87722', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
            {group.name.charAt(0)}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>{group.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>{group.entries.length} entr{group.entries.length !== 1 ? 'ies' : 'y'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>{fmtHours(group.totalSec)}</div>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: 'var(--text-muted)', flexShrink: 0 }}>
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>

      {open && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {groupBy === 'person' ? (
                  <th style={th}>Job</th>
                ) : (
                  <th style={th}>Worker</th>
                )}
                <th style={th}>Date</th>
                <th style={th}>Start</th>
                <th style={th}>End</th>
                <th style={th}>Note</th>
                <th style={{ ...th, textAlign: 'right' }}>Hours</th>
              </tr>
            </thead>
            <tbody>
              {group.entries
                .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                .map((s, i) => (
                <tr key={s.id} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
                  <td style={td}>
                    {groupBy === 'person' ? (
                      <span style={{ fontWeight: 500, color: 'var(--text)' }}>{s.jobcodeName}</span>
                    ) : (
                      <span style={{ fontWeight: 500, color: 'var(--text)' }}>{s.userName}</span>
                    )}
                  </td>
                  <td style={td}>{fmtDate(s.date)}</td>
                  <td style={td}>{fmtTime(s.start)}</td>
                  <td style={td}>{s.onTheClock ? <span style={{ color: '#16a34a', fontWeight: 600 }}>Active</span> : fmtTime(s.end)}</td>
                  {/* The note on the QuickBooks timesheet — written from the phone at clock-in,
                      mid-shift or clock-out ("No lunch"), the same one payroll reads. */}
                  <td style={{ ...td, maxWidth: 280, whiteSpace: 'normal' }} title={s.notes || ''}>
                    {s.notes ? <span style={{ color: '#92400e', fontWeight: 600 }}>📝 {s.notes}</span> : <span style={{ color: '#d1d5db' }}>—</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--text)' }}>{fmtHours(s.duration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: '8px 14px', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' };
